import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, Sparkles, Info, Copy, Check, Code2, FileText, Edit3 } from 'lucide-react';
import { generateAIRecommendations, generateAssistantResponse } from '../../lib/openai';
import { replaceSnippets, updateSnippetData } from '../../lib/prompt-snippets';
import { isStopWord } from '../../lib/stop-words';
import { toast } from '../../lib/toast';
import { supabaseClient } from '../../lib/supabase';
import { LoadingScreen } from '../common/LoadingScreen';
import { useFilteredKeywords } from '../../contexts/FilteredKeywordsContext';
import { ArticleGenerator } from './ArticleGenerator';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface Prompt {
  id: string | number;
  name: string;
  description: string;
  content: string;
  category: string;
}

const SNIPPETS = [
  {
    name: '{{Missing words}}',
    description: 'Пропущені ключі з вкладки Keywords'
  },
  {
    name: '{{Present words}}',
    description: 'Наявні ключі з вкладки Keywords'
  },
  {
    name: '{{Keywords}}',
    description: 'SEO Keywords з вкладки Keywords'
  },
  {
    name: '{{Headers}}',
    description: 'Заголовки конкурентів з вкладки Competitors'
  },
  {
    name: '{{Text}}',
    description: 'Текст з редактору'
  },
  {
    name: '{{Resultai}}',
    description: 'Текст з поля відповіді AI'
  },
  {
    name: '{{Description}}',
    description: 'Опис компанії з налаштувань проекту'
  },
  {
    name: '{{Language}}',
    description: '🆕 Мова контенту (uk, ru, en) - АВТОМАТИЧНО'
  },
  {
    name: '{{Keyword}}',
    description: '🆕 Основне ключове слово - АВТОМАТИЧНО'
  },
  {
    name: '{{Intent}}',
    description: '🆕 Інтент (commercial, informational, navigational, transactional) - АВТОМАТИЧНО'
  },
  {
    name: '{{ContentType}}',
    description: '🆕 Тип контенту (category, blog-best, blog-how-to, etc.) - АВТОМАТИЧНО'
  },
  {
    name: '{{StructureRecommendations}}',
    description: '🆕 Рекомендації по структурі на основі аналізу - АВТОМАТИЧНО'
  },
  {
    name: '{{CompetitorInsights}}',
    description: '🆕 Інсайти з SERP конкурентів - АВТОМАТИЧНО'
  }
];

function getPromptPreview(text: string, maxLength = 150): string {
  const cleanText = text.replace(/\{\{.*?\}\}/g, '')
    .replace(/\[.*?\]/g, '')
    .replace(/\n+/g, ' ')
    .trim();
  
  if (cleanText.length <= maxLength) {
    return cleanText;
  }
  
  const lastSpace = cleanText.lastIndexOf(' ', maxLength);
  return lastSpace > 0 ? cleanText.slice(0, lastSpace) + '...' : cleanText.slice(0, maxLength) + '...';
}

type GenerationMode = 'prompt' | 'article';

function AIPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { filteredMissingKeywords, filteredPresentKeywords } = useFilteredKeywords();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [promptText, setPromptText] = useState('');
  const [aiResult, setAiResult] = useState('');
  const [showSnippets, setShowSnippets] = useState(false);
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [chatHistory, setChatHistory] = useState<{
    messages: Message[];
    context: string;
  }>({
    messages: [],
    context: ''
  });
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isCopied, setIsCopied] = useState(false);
  const [generationMode, setGenerationMode] = useState<GenerationMode>('article');
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'formatted' | 'html'>('formatted');

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Removed: updateSnippetData on aiResult change to prevent infinite loop
  // The snippet data is already updated when needed during generation

  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('prompts')
          .select('*')
          .order('category', { ascending: true })
          .order('name', { ascending: true });

        if (error) throw error;
        setPrompts(data || []);
      } catch (err) {
        console.error('Error loading prompts:', err);
        setError(err instanceof Error ? err.message : 'Failed to load prompts');
      }
    };

    loadPrompts();
  }, []);

  useEffect(() => {
    if (selectedPrompt) {
      const prompt = prompts.find(p => p.id.toString() === selectedPrompt);
      if (prompt) {
        setPromptText(prompt.content);
      }
    }
  }, [selectedPrompt, prompts]);

  useEffect(() => {
    const loadAnalysis = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const { data: analysis, error: fetchError } = await supabaseClient
          .from('analyses')
          .select(`
            *,
            project:projects (
              id,
              name,
              description,
              project_prompt
            )
          `)
          .eq('id', id)
          .single();

        if (fetchError) throw fetchError;

        // Store analysis data for ArticleGenerator
        setAnalysisData(analysis);

        // Використовуємо опис проекту для контексту AI
        const projectDescription = analysis?.project?.description || 
                                   'No description available';
        console.log('Loaded project description:', projectDescription);
        console.log('Loaded project description:', projectDescription);
        
        // Оновлюємо snippet data з повною інформацією для розумної генерації
        updateSnippetData({
          description: projectDescription,
          language: analysis?.language || 'uk',
          keyword: analysis?.keyword || '',
          serpResults: analysis?.serp_results || []
        });

        // Використовуємо дані з KeywordsTab через контекст
        console.log('🔴 AIPage - Using filtered keywords from context');
        console.log('🔴 AIPage - Missing keywords from context:', filteredMissingKeywords);
        console.log('🔴 AIPage - Present keywords from context:', filteredPresentKeywords);

        updateSnippetData({
          keywords: {
            missing: filteredMissingKeywords,
            present: filteredPresentKeywords,
            seo: analysis?.additional_keywords || []
          }
        });

        if (analysis?.content_analysis?.competitors) {
          const selectedDomains = analysis.content_analysis.selectedDomains || [];
          
          const headers = analysis.content_analysis.competitors
            .filter((competitor: any) => 
              competitor?.domain && selectedDomains.includes(competitor.domain)
            )
            .flatMap((competitor: any) => {
              if (!competitor?.headers) return [];
              
              return Object.entries(competitor.headers).map(([level, texts]: [string, any]) => 
                texts.map((text: string) => `${competitor.domain} (${level}): ${text}`)
              ).flat();
            });

          updateSnippetData({ headers });
        }

        if (analysis?.editor_content) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = analysis.editor_content;
          updateSnippetData({
            text: tempDiv.textContent?.trim() || ''
          });
        }

        if (analysis?.ai_result) {
          setAiResult(analysis.ai_result);
        }

      } catch (err) {
        console.error('Error loading analysis:', err);
        setError(err instanceof Error ? err.message : 'Failed to load analysis');
      } finally {
        setLoading(false);
      }
    };

    loadAnalysis();
  }, [id]);

  // Оновлюємо snippet data коли змінюються відфільтровані ключі з контексту
  useEffect(() => {
    console.log('🔴 AIPage - Keywords from context changed');
    console.log('🔴 AIPage - Missing:', filteredMissingKeywords);
    console.log('🔴 AIPage - Present:', filteredPresentKeywords);
    
    updateSnippetData({
      keywords: {
        missing: filteredMissingKeywords,
        present: filteredPresentKeywords,
        seo: [] // Additional keywords залишаємо як є
      }
    });
  }, [filteredMissingKeywords, filteredPresentKeywords]);

  useEffect(() => {
    const loadChatHistory = async () => {
      if (!id) return;

      try {
        const { data, error } = await supabaseClient
          .from('analyses')
          .select('chat_history')
          .eq('id', id)
          .single();

        if (error) throw error;

        if (data?.chat_history) {
          setChatHistory(data.chat_history);
          setChatMessages(data.chat_history.messages);
        }
      } catch (err) {
        console.error('Error loading chat history:', err);
      }
    };

    loadChatHistory();
  }, [id]);

  const handlePreviewPrompt = () => {
    if (!promptText.trim()) {
      toast.error('Please enter or select a prompt first');
      return;
    }

    try {
      const processedPrompt = replaceSnippets(promptText);
      setPromptText(processedPrompt);
      toast.success('Prompt preview updated with actual data');
    } catch (err) {
      console.error('Error previewing prompt:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to preview prompt');
    }
  };

  const generateRecommendations = async () => {
    if (!promptText.trim() || isGeneratingAI || !id) return;

    setIsGeneratingAI(true);
    const loadingToast = toast.loading('Generating AI recommendations...');

    try {
      const result = await generateAIRecommendations(promptText);
      setAiResult(result);

      const newHistory = {
        messages: chatMessages,
        context: result
      };

      const { error: updateError } = await supabaseClient
        .from('analyses')
        .update({ 
          chat_history: newHistory,
          ai_result: result 
        })
        .eq('id', id);

      if (updateError) throw updateError;

      setChatHistory(newHistory);
      toast.success('AI recommendations generated!', { id: loadingToast });
    } catch (err) {
      console.error('Error generating AI recommendations:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to generate AI recommendations', { id: loadingToast });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || isSendingMessage || !id) return;

    setIsSendingMessage(true);
    const loadingToast = toast.loading('Sending message...');

    try {
      const newMessage: Message = { role: 'user', content: chatInput };
      const updatedMessages = [...chatMessages, newMessage];
      setChatMessages(updatedMessages);
      setChatInput('');

      const context = aiResult || chatHistory.context;

      const response = await generateAssistantResponse(
        [
          { role: 'system', content: `Previous context: ${context}` },
          ...updatedMessages
        ],
        id
      );

      const finalMessages: Message[] = [...updatedMessages, { role: 'assistant', content: response }];
      setChatMessages(finalMessages);

      const newHistory = {
        messages: finalMessages,
        context: context
      };

      const { error: updateError } = await supabaseClient
        .from('analyses')
        .update({ chat_history: newHistory })
        .eq('id', id);

      if (updateError) throw updateError;

      setChatHistory(newHistory);
      toast.success('Response received', { id: loadingToast });

    } catch (err) {
      console.error('Error sending message:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to send message', { id: loadingToast });
    } finally {
      setIsSendingMessage(false);
    }
  };

  const clearChatHistory = async () => {
    if (!id) return;

    try {
      setChatMessages([]);
      
      const emptyHistory = {
        messages: [],
        context: aiResult || ''
      };
      
      const { error: updateError } = await supabaseClient
        .from('analyses')
        .update({ 
          chat_history: emptyHistory
        })
        .eq('id', id);

      if (updateError) throw updateError;

      setChatHistory(emptyHistory);
      
      toast.success('Chat history cleared');
    } catch (err) {
      console.error('Error clearing chat history:', err);
      toast.error('Failed to clear chat history');
      
      setChatMessages(chatHistory.messages);
    }
  };

  const handleCopyResult = async () => {
    if (!aiResult) return;
    
    try {
      // Copy HTML to clipboard with formatting
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([aiResult], { type: 'text/html' }),
          'text/plain': new Blob([aiResult.replace(/<[^>]*>/g, '')], { type: 'text/plain' })
        })
      ]);
      
      setIsCopied(true);
      toast.success('Content copied with formatting');
      
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy content:', err);
      toast.error('Failed to copy content');
    }
  };

  const handleAddToEditor = async () => {
    if (!aiResult || !id) return;

    try {
      const loadingToast = toast.loading('Adding to editor...');
      
      // Clean HTML - remove empty paragraphs and normalize spacing
      const cleanHtml = (html: string): string => {
        // First, aggressively remove all newlines and extra spaces from the HTML source
        let cleanedHtml = html
          .replace(/\r\n/g, '') // Remove Windows line breaks
          .replace(/\n/g, '') // Remove Unix line breaks
          .replace(/\r/g, '') // Remove Mac line breaks
          .replace(/\s{2,}/g, ' '); // Replace multiple spaces with single space
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(cleanedHtml, 'text/html');
        
        // Remove empty paragraphs and unnecessary whitespace
        doc.querySelectorAll('p').forEach(p => {
          const text = p.textContent?.trim() || '';
          if (!text || text === '&nbsp;' || text === '\u00A0') {
            p.remove();
          }
        });
        
        // Remove all <br> tags
        doc.querySelectorAll('br').forEach(br => br.remove());
        
        // Get cleaned HTML and do final cleanup
        cleanedHtml = doc.body.innerHTML;
        cleanedHtml = cleanedHtml
          .replace(/<p><\/p>/gi, '') // Remove empty p tags
          .replace(/>\s+</g, '><') // Remove all whitespace between tags
          .trim();
        
        console.log('🔍 Cleaned HTML contains tables:', cleanedHtml.includes('<table'));
        
        return cleanedHtml;
      };
      
      const cleanedHtml = cleanHtml(aiResult);
      
      console.log('📊 Final cleaned HTML sample:', cleanedHtml.substring(0, 1000));
      console.log('📊 Total HTML length:', cleanedHtml.length);
      
      const wordCount = cleanedHtml.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;

      // Update database - save cleaned HTML to editor_content field (what TextEditor reads)
      const { error: updateError } = await supabaseClient
        .from('analyses')
        .update({
          editor_content: cleanedHtml,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

      toast.success('Content added to editor!', { id: loadingToast });
      
      // Navigate to editor
      setTimeout(() => {
        navigate(`/analyses/${id}/editor`);
      }, 500);
    } catch (err) {
      console.error('Error adding to editor:', err);
      toast.error('Failed to add content to editor');
    }
  };

  const handleArticleComplete = useCallback(async (html: string) => {
    console.log('📄 Article completed, HTML length:', html.length);
    console.log('📄 First 500 chars:', html.substring(0, 500));
    console.log('📄 Contains HTML tags:', /<[^>]+>/.test(html));
    console.log('📄 Contains escaped HTML:', /&lt;|&gt;/.test(html));
    
    // Decode HTML entities if present
    let cleanHtml = html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
    
    console.log('📄 After decode, first 500 chars:', cleanHtml.substring(0, 500));
    
    setAiResult(cleanHtml);

    // Update snippet data once
    updateSnippetData({ result: cleanHtml });

    // Save to database
    if (!id) return;

    try {
      const { error: updateError } = await supabaseClient
        .from('analyses')
        .update({ 
          ai_result: cleanHtml 
        })
        .eq('id', id);

      if (updateError) throw updateError;
    } catch (err) {
      console.error('Error saving article:', err);
      toast.error('Article generated but failed to save');
    }
  }, [id]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">AI Recommendations</h2>
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Mode Selector */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setGenerationMode('article')}
                className={`${
                  generationMode === 'article'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                🚀 Multi-Stage Article Generator
              </button>
              <button
                onClick={() => setGenerationMode('prompt')}
                className={`${
                  generationMode === 'prompt'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                ✨ Custom Prompts
              </button>
            </nav>
          </div>

          {/* Article Generator Mode */}
          {generationMode === 'article' && analysisData && (
            <ArticleGenerator
              analysisId={id!}
              competitors={analysisData.content_analysis?.competitors || []}
              keywords={{
                additional: analysisData.additional_keywords || [],
                missing: filteredMissingKeywords,
                present: filteredPresentKeywords
              }}
              projectDescription={analysisData.project?.description || ''}
              projectPrompt={analysisData.project?.project_prompt}
              selectedDomains={analysisData.content_analysis?.selectedDomains || []}
              language={analysisData.language || 'uk'}
              keyword={analysisData.keyword || ''}
              url={analysisData.url || ''}
              serpResults={analysisData.serp_results || []}
              onComplete={handleArticleComplete}
            />
          )}

          {/* Prompt Mode */}
          {generationMode === 'prompt' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Info className="h-5 w-5 text-blue-500 mr-2" />
                <h3 className="text-sm font-medium text-blue-900">Available Snippets</h3>
              </div>
              <button
                onClick={() => setShowSnippets(!showSnippets)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showSnippets ? 'Hide' : 'Show'} Details
              </button>
            </div>
            {showSnippets && (
              <div className="mt-2 space-y-2">
                {SNIPPETS.map((snippet) => (
                  <div key={snippet.name} className="flex items-start space-x-2 text-sm">
                    <code className="px-2 py-1 bg-blue-100 rounded text-blue-800 font-mono">
                      {snippet.name}
                    </code>
                    <span className="text-blue-700">-</span>
                    <span className="text-blue-800 flex-1">{snippet.description}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Prompt
                  </label>
                  <select
                    value={selectedPrompt || ''}
                    onChange={(e) => setSelectedPrompt(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Choose a prompt...</option>
                    {prompts.map((prompt) => (
                      <option key={prompt.id} value={prompt.id}>
                        {prompt.name} - {getPromptPreview(prompt.content)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Prompt Text
                    </label>
                    <button
                      onClick={handlePreviewPrompt}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      title="Preview with actual data"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Preview
                    </button>
                  </div>
                  <textarea
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm resize-y"
                    placeholder="Enter or select a prompt..."
                    rows={6}
                  />
                </div>
              </div>

              <button
                onClick={generateRecommendations}
                disabled={!promptText.trim() || isGeneratingAI}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Recommendations
              </button>
            </div>

            <div className="border-l border-gray-200 pl-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Chat</h3>
                  {chatMessages.length > 0 && (
                    <button
                      onClick={clearChatHistory}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Clear History
                    </button>
                  )}
                </div>
                
                <div className="h-[500px] overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                  {chatMessages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <p>No messages yet. Start a conversation!</p>
                      <p className="text-sm mt-2">The AI will use the generated result as context.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {chatMessages.map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg p-3 ${
                              message.role === 'user'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            <div className="text-xs mb-1 opacity-75">
                              {message.role === 'user' ? 'You' : 'AI Assistant'}
                            </div>
                            <div
                              className="prose prose-sm"
                              dangerouslySetInnerHTML={{ 
                                __html: message.role === 'assistant' 
                                  ? message.content 
                                  : `<p>${message.content}</p>` 
                              }}
                            />
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!chatInput.trim() || isSendingMessage}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {isSendingMessage ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      'Send'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
            </>
          )}

          {/* AI Result Display (shown for both modes) */}
          {aiResult && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Generated Content
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode(viewMode === 'formatted' ? 'html' : 'formatted')}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
                    title={viewMode === 'formatted' ? 'Show HTML code' : 'Show formatted text'}
                  >
                    {viewMode === 'formatted' ? (
                      <>
                        <Code2 className="h-3 w-3 mr-1" />
                        HTML
                      </>
                    ) : (
                      <>
                        <FileText className="h-3 w-3 mr-1" />
                        Preview
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCopyResult}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
                    title="Copy with formatting"
                  >
                    {isCopied ? (
                      <Check className="h-3 w-3 mr-1" />
                    ) : (
                      <Copy className="h-3 w-3 mr-1" />
                    )}
                    {isCopied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    onClick={handleAddToEditor}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded shadow-sm"
                    title="Add to editor and open"
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Add to Editor
                  </button>
                </div>
              </div>
              <div className="h-[600px] w-full border border-gray-200 rounded-md overflow-hidden bg-white">
                {viewMode === 'formatted' ? (
                  <div 
                    className="p-4 h-full overflow-y-auto w-full [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-5 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mb-2 [&_h3]:mt-4 [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:mb-2 [&_h4]:mt-3 [&_p]:mb-4 [&_p]:leading-7 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-4 [&_li]:mb-1 [&_strong]:font-bold [&_em]:italic [&_a]:text-blue-600 [&_a]:underline"
                    style={{
                      fontSize: '0.875rem',
                      color: '#374151'
                    }}
                    dangerouslySetInnerHTML={{ __html: aiResult }}
                  />
                ) : (
                  <pre className="p-4 bg-gray-50 h-full overflow-y-auto text-xs w-full m-0 whitespace-pre-wrap break-words">
                    <code className="block w-full">{aiResult}</code>
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIPage;